import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const ACCREDITATION_CRITERIA = [
  {
    id: "net_assets",
    label: "Net Assets Threshold",
    description: "Organisation has net assets exceeding £5 million (or equivalent)",
    icon: "PoundSterling",
  },
  {
    id: "professional_client",
    label: "Professional Client Classification",
    description: "Meets MiFID II / FCA definition of a professional client or eligible counterparty",
    icon: "Award",
  },
  {
    id: "qualified_investor",
    label: "Qualified Investor Status",
    description: "Qualifies as a qualified investor under the Prospectus Regulation",
    icon: "BadgeCheck",
  },
  {
    id: "investment_experience",
    label: "Investment Experience",
    description: "Has made at least 10 significant transactions in infrastructure or private markets in the past 4 quarters",
    icon: "TrendingUp",
  },
];

const INVESTMENT_LIMITS = [
  { label: "Single Deal Limit", value: "Up to £50m per transaction", icon: "ArrowUpRight" },
  { label: "Annual Commitment", value: "Up to £200m per annum", icon: "Calendar" },
  { label: "Concentration Limit", value: "Max 25% in single sector", icon: "PieChart" },
  { label: "Leverage Limit", value: "Up to 2x NAV", icon: "BarChart2" },
];

export default function StepAccreditation({ data, onChange }) {
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(data?.accreditationVerified || false);

  const handleVerify = () => {
    setVerifying(true);
    setTimeout(() => {
      setVerifying(false);
      setVerified(true);
      onChange({ ...data, accreditationVerified: true });
    }, 2000);
  };

  const handleCriteriaToggle = (id) => {
    const current = data?.accreditationCriteria || [];
    const updated = current?.includes(id)
      ? current?.filter((c) => c !== id)
      : [...current, id];
    onChange({ ...data, accreditationCriteria: updated });
  };

  const selectedCriteria = data?.accreditationCriteria || [];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-[rgba(59,76,76,0.07)] border border-blue-100">
        <Icon name="ShieldCheck" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-caption font-semibold text-sm text-primary mb-1">Qualified Investor Verification</p>
          <p className="text-xs text-muted-foreground">
            SeedDrive is restricted to qualified and professional investors under FCA regulations. Please confirm which criteria your organisation meets. Verification is automated and typically completes within minutes.
          </p>
        </div>
      </div>
      {/* Criteria selection */}
      <div>
        <h4 className="font-caption font-semibold text-sm text-foreground mb-3">
          Accreditation Criteria <span className="text-muted-foreground font-normal">(select all that apply)</span>
        </h4>
        <div className="space-y-2">
          {ACCREDITATION_CRITERIA?.map((criterion) => {
            const isSelected = selectedCriteria?.includes(criterion?.id);
            return (
              <button
                key={criterion?.id}
                onClick={() => handleCriteriaToggle(criterion?.id)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-start gap-3 ${
                  isSelected
                    ? "border-primary bg-[rgba(59,76,76,0.07)]" :"border-border bg-card hover:border-secondary"
                }`}
              >
                <div
                  className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md ${
                    isSelected ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <Icon
                    name={criterion?.icon}
                    size={16}
                    color={isSelected ? "white" : "var(--color-muted-foreground)"}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-caption font-semibold text-sm ${isSelected ? "text-primary" : "text-foreground"}`}>
                    {criterion?.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{criterion?.description}</p>
                </div>
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    isSelected ? "border-primary bg-primary" : "border-border"
                  }`}
                >
                  {isSelected && <Icon name="Check" size={12} color="white" strokeWidth={3} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Investment limits placeholder */}
      <div className="p-4 rounded-lg border border-border bg-card">
        <h4 className="font-caption font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
          <Icon name="Info" size={14} color="var(--color-muted-foreground)" />
          Indicative Investment Limits
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {INVESTMENT_LIMITS?.map((limit) => (
            <div key={limit?.label} className="flex items-center gap-2">
              <Icon name={limit?.icon} size={14} color="var(--color-secondary)" />
              <div>
                <p className="text-xs text-muted-foreground">{limit?.label}</p>
                <p className="font-data text-sm font-semibold text-foreground">{limit?.value}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3 italic">
          * Limits are indicative and subject to review based on your organisation's profile and regulatory requirements.
        </p>
      </div>
      {/* Verification button */}
      {!verified ? (
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm font-caption text-muted-foreground text-center">
            Once you have confirmed your criteria above, click below to initiate automated accreditation verification.
          </p>
          <Button
            variant="default"
            size="lg"
            iconName="ShieldCheck"
            iconPosition="left"
            loading={verifying}
            onClick={handleVerify}
            disabled={selectedCriteria?.length === 0}
          >
            {verifying ? "Verifying..." : "Run Accreditation Check"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-4 p-6 rounded-xl border-2 border-green-300 bg-green-50">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <Icon name="CheckCircle2" size={36} color="var(--color-success)" />
          </div>
          <div className="text-center">
            <p className="font-heading font-semibold text-lg text-success">Accreditation Verified</p>
            <p className="text-sm text-muted-foreground mt-1">
              Your organisation meets the qualified investor criteria. You may proceed to complete onboarding.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {selectedCriteria?.map((id) => {
              const criterion = ACCREDITATION_CRITERIA?.find((c) => c?.id === id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-success text-xs font-caption"
                >
                  <Icon name="Check" size={10} />
                  {criterion?.label}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}